import { Box, Typography, Paper, Grid, Chip, Avatar, IconButton, Divider, Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import type { ChipColor } from '@/types/chip';
import { Business, Email, Phone, LocationOn, Close } from '@mui/icons-material';
import { PageDescription } from '@/components/help';
import { Show, useGetList } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { StudentRecord } from '../shared/types';
import type { TutoringAgreementRecord, ServiceAuthorisationRecord } from '@/types/entities';

const STATUS_COLORS: Record<string, ChipColor> = {
  Draft: 'default',
  Active: 'success',
  Inactive: 'warning',
};

export const ClientDetail = () => {
  const navigate = useNavigate();
  const { data: students } = useGetList('students');
  const { data: agreements } = useGetList('tutoring-agreements');
  const { data: serviceAuths } = useGetList('service-authorisations');

  return (
    <Show render={({ record }) => {
      if (!record) return null;

      const clientStudents = (students || []).filter((s: StudentRecord) => s.parent?.id === record.id).slice(0, 10);
      const clientAgreements = (agreements || []).filter((a: TutoringAgreementRecord) => a.client?.id === record.id).slice(0, 10);
      const clientServiceAuths = (serviceAuths || []).filter((sa: ServiceAuthorisationRecord) => sa.tutor?.id === record.id).slice(0, 10);

      return (
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <IconButton onClick={() => navigate('/clients')} size="small" sx={{ color: 'text.secondary' }} aria-label="Close">
              <Close />
            </IconButton>
          </Box>

          <PageDescription
            entity="Client"
            description="An organisation (school, training centre, or corporate entity) that receives tutoring services."
            icon={<Business />}
          />

          <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #0277bd, #0288d1)', color: 'primary.contrastText', borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 72, height: 72, bgcolor: 'secondary.main', fontSize: 28 }}>
                <Business />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700}>{record.name}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{record.email}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip label={record.status} size="small" color={STATUS_COLORS[record.status]} />
                </Box>
              </Box>
            </Box>
          </Paper>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Business color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>Client Information</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{record.email || '—'}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{record.phone || '—'}</Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="caption" color="text.secondary">Contact Person</Typography>
                    <Typography variant="body1">{record.legalEntity?.companyName || '—'}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>



            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Service Authorisations</Typography>
                  <Chip label={`${clientServiceAuths.length} records`} size="small" sx={{ ml: 1 }} />
                  {clientServiceAuths.length > 0 && (
                    <Button size="small" sx={{ ml: 'auto', textTransform: 'none' }} onClick={() => navigate('/service-authorisations')}>
                      View All
                    </Button>
                  )}
                </Box>
                {clientServiceAuths.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Authorisation #</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Valid From</TableCell>
                        <TableCell>Valid Until</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientServiceAuths.map((sa: ServiceAuthorisationRecord) => (
                        <TableRow key={sa.id}>
                          <TableCell><Chip label={sa.tutor?.id ?? '—'} size="small" /></TableCell>
                          <TableCell>{sa.service?.id || '—'}</TableCell>
                          <TableCell>{sa.effectiveFrom || '—'}</TableCell>
                          <TableCell>{sa.effectiveTo || '—'}</TableCell>
                          <TableCell><Chip label={sa.status} size="small" color={sa.status === 'Active' ? 'success' : 'default'} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No service authorisations found</Typography>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Students</Typography>
                  <Chip label={`${clientStudents.length} records`} size="small" sx={{ ml: 1 }} />
                </Box>
                {clientStudents.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Student</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientStudents.map((s: StudentRecord) => (
                        <TableRow key={s.id}>
                          <TableCell>{s.legalEntity?.firstName ?? ""} {s.legalEntity?.lastName ?? ""}</TableCell>
                          <TableCell>{s.legalEntity?.email || '—'}</TableCell>
                          <TableCell><Chip label={s.status} size="small" color={s.status === 'Active' ? 'success' : 'default'} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No students found</Typography>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Tutoring Agreements</Typography>
                  <Chip label={`${clientAgreements.length} records`} size="small" sx={{ ml: 1 }} />
                </Box>
                {clientAgreements.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Agreement #</TableCell>
                        <TableCell>Tutor</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {clientAgreements.map((a: TutoringAgreementRecord) => (
                        <TableRow key={a.id}>
                          <TableCell><Chip label={a.id} size="small" /></TableCell>
                          <TableCell>{a.tutor?.id || '—'}</TableCell>
                          <TableCell>{a.effectiveFrom}</TableCell>
                          <TableCell>{a.effectiveTo || '—'}</TableCell>
                          <TableCell><Chip label={a.status} size="small" color={a.status === 'Active' ? 'success' : 'default'} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" color="text.secondary">No tutoring agreements found</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      );
    }} />
  );
};
